/**
 * PromotionalCostManagement.jsx — 홍보물원가 관리 페이지 (어셈블리)
 *
 * 발주처 관리(SupplierSection)와 홍보물 원가 관리(ItemSection)를 조립하고,
 * 상태·핸들러·API 호출을 담당한다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import { SupplierSection } from './SupplierSection';
import { ItemSection }     from './ItemSection';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from '../adminlayout/designStandard';

// ── 상수 ─────────────────────────────────────────────────────────────────────
// ── 빈 폼 생성 함수 ──────────────────────────────────────────────────────────
function emptySupplier() {
  return { id: null, name: '', default_courier: '', is_active: true };
}
function emptyItem() {
  return { id: null, name: '', price: '', cost_price: '', supplier_id: '', is_active: true, no_shipping: false };
}

// ── 범용 정렬 함수 (문자열·숫자 자동 판별, null은 맨 뒤) ────────────────────
function applySortFn(arr, { key, dir }) {
  return [...arr].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), 'ko');
    return dir === 'asc' ? cmp : -cmp;
  });
}

// 테이블 헤더 클릭 시 정렬 상태 토글 (같은 키 재클릭 시 방향 반전)
function toggleSort(current, key, setter) {
  setter((prev) =>
    prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }
  );
}

export default function PromotionalCostManagement() {
  const { showToast } = useToast();
  // ── 데이터 ────────────────────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState([]);  // 발주처 목록
  const [items,     setItems]     = useState([]);  // 홍보물 목록 (원가 포함)

  // ── 발주처 편집 상태 ──────────────────────────────────────────────────────
  const [editingSupplier,         setEditingSupplier]         = useState(null);
  const [confirmDeleteSupplierId, setConfirmDeleteSupplierId] = useState(null);

  // ── 홍보물 편집 상태 ──────────────────────────────────────────────────────
  const [editingItem, setEditingItem] = useState(null);

  // ── 정렬 상태 ─────────────────────────────────────────────────────────────
  const [supplierSort, setSupplierSort] = useState({ key: 'name', dir: 'asc' });
  const [itemSort,     setItemSort]     = useState({ key: 'name', dir: 'asc' });

  // ── 이력 accordion 상태 ───────────────────────────────────────────────────
  const [historyMap,    setHistoryMap]    = useState({});  // itemId → history 배열
  const [openHistoryId, setOpenHistoryId] = useState(null);

  // ── 저장 중 플래그 ────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);

  // ── 화면 크기 변화 감지 (표준 헤더 반응형) ────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── 발주처 목록 조회 ──────────────────────────────────────────────────────
  const fetchSuppliers = useCallback(async () => {
    const res = await apiClient.get('/api/admin/promotional-suppliers');
    if (res.ok) setSuppliers(Array.isArray(res.data?.data) ? res.data.data : []);
    else showToast('발주처 목록 조회 실패', 'error');
  }, [showToast]);

  // ── 홍보물 목록 조회 (원가·발주처 포함) ──────────────────────────────────
  const fetchItems = useCallback(async () => {
    const res = await apiClient.get('/api/admin/promotional-items');
    if (res.ok) setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    else showToast('홍보물 목록 조회 실패', 'error');
  }, [showToast]);

  // 초기 데이터 로드
  useEffect(() => { fetchSuppliers(); fetchItems(); }, [fetchSuppliers, fetchItems]);

  // ── 발주처 저장 ──────────────────────────────────────────────────────────
  async function saveSupplier() {
    if (!editingSupplier) return;
    if (!editingSupplier.name.trim()) { showToast('발주처명을 입력하세요.', 'error'); return; }
    setSaving(true);
    const res = await apiClient.put('/api/admin/promotional-suppliers', { suppliers: [{ ...editingSupplier }] });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast('저장되었습니다.', 'success');
    setEditingSupplier(null);
    fetchSuppliers();
    fetchItems(); // supplier 이름 JOIN으로 items에도 영향
  }

  // ── 발주처 삭제 ──────────────────────────────────────────────────────────
  async function deleteSupplier(id) {
    setSaving(true);
    // apiClient는 delete 예약어 대신 del 메서드를 사용
    const res = await apiClient.del(`/api/admin/promotional-suppliers/${id}`);
    setSaving(false);
    setConfirmDeleteSupplierId(null);
    if (!res.ok) { showToast(res.data?.message || '삭제 실패', 'error'); return; }
    showToast('발주처가 삭제되었습니다.', 'success');
    fetchSuppliers();
    fetchItems(); // 연결된 홍보물 supplier 정보 갱신
  }

  // ── 홍보물 저장 ──────────────────────────────────────────────────────────
  async function saveItem() {
    if (!editingItem) return;
    if (!editingItem.name.trim()) { showToast('홍보물명을 입력하세요.', 'error'); return; }
    setSaving(true);
    const payload = {
      items: [{
        id:          editingItem.id || undefined,
        name:        editingItem.name.trim(),
        price:       Number(editingItem.price) || 0,
        cost_price:  Number(editingItem.cost_price) || 0,
        supplier_id: editingItem.supplier_id ? Number(editingItem.supplier_id) : null,
        is_active:   editingItem.is_active,
        no_shipping: Boolean(editingItem.no_shipping),
      }],
    };
    const res = await apiClient.put('/api/admin/promotional-items', payload);
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast('저장되었습니다.', 'success');
    setEditingItem(null);
    fetchItems();
    // 이력 갱신 (이미 열려있는 경우)
    if (editingItem.id && openHistoryId === editingItem.id) {
      fetchHistory(editingItem.id, true);
    }
  }

  // ── 이력 조회 ────────────────────────────────────────────────────────────
  async function fetchHistory(itemId, force = false) {
    if (!force && historyMap[itemId] !== undefined) return; // 이미 로드됨
    const res = await apiClient.get(`/api/admin/promotional-items/${itemId}/history`);
    if (res.ok) {
      setHistoryMap((p) => ({ ...p, [itemId]: Array.isArray(res.data?.data) ? res.data.data : [] }));
    }
  }

  // 이력 accordion 토글
  function toggleHistory(itemId) {
    if (openHistoryId === itemId) {
      setOpenHistoryId(null);
    } else {
      setOpenHistoryId(itemId);
      fetchHistory(itemId);
    }
  }

  // ── 발주처 변경 → 기본택배사 미리보기 업데이트 ──────────────────────────
  function handleItemSupplierChange(supplierId) {
    setEditingItem((p) => ({ ...p, supplier_id: supplierId }));
  }

  // ── 정렬된 목록 및 파생값 ────────────────────────────────────────────────
  const sortedSuppliers = applySortFn(suppliers, supplierSort);
  const sortedItems     = applySortFn(items, itemSort);
  const activeSuppliers = suppliers.filter((s) => s.is_active);
  // 편집 중 선택된 supplier의 기본택배사 미리보기
  const previewCourier  = editingItem?.supplier_id
    ? (suppliers.find((s) => s.id === Number(editingItem.supplier_id))?.default_courier || '')
    : '';

  return (
    <div>
      {/* 페이지 헤더 — 직원 관리 표준: 텍스트 제목/부제 (designStandard) */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>홍보물원가 관리</h2>
          <div style={stdPageSubtitle}>홍보물 원가를 관리합니다.</div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 20 }}>
      {/* 발주처 관리 섹션 */}
      <SupplierSection
        sortedSuppliers={sortedSuppliers}
        supplierSort={supplierSort}
        onSortChange={(key) => toggleSort(supplierSort, key, setSupplierSort)}
        editingSupplier={editingSupplier}
        setEditingSupplier={setEditingSupplier}
        confirmDeleteId={confirmDeleteSupplierId}
        setConfirmDeleteId={setConfirmDeleteSupplierId}
        saving={saving}
        onSave={saveSupplier}
        onDelete={deleteSupplier}
        emptySupplier={emptySupplier}
      />

      {/* 홍보물 원가 관리 섹션 */}
      <ItemSection
        sortedItems={sortedItems}
        itemSort={itemSort}
        onSortChange={(key) => toggleSort(itemSort, key, setItemSort)}
        historyMap={historyMap}
        openHistoryId={openHistoryId}
        onToggleHistory={toggleHistory}
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        activeSuppliers={activeSuppliers}
        previewCourier={previewCourier}
        onSupplierChange={handleItemSupplierChange}
        saving={saving}
        onSave={saveItem}
        emptyItem={emptyItem}
      />
      </div>
    </div>
  );
}
